"use client";

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import PortfolioAllocation from "./PortfolioAllocation";
import GrowthOfOne from "./GrowthOfOne";
import AssetsHistory from "./AssetsHistory";
import ProtocolApyHistory from "./ProtocolApyHistory";
import OverviewStats from "./OverviewStats";
import CurrentHoldings from "./CurrentHoldings";
import DashboardCharts from "./DashboardCharts";
import { useDashboardData } from "../context/DashboardDataContext";
import { computeSharpeRatioFromDailyGrowth } from "./utils/sharpe";
import styles from "./dashboard.module.css";

const HOLDING_COLORS = [
  "#1a2b5f",
  "#2c4a8c",
  "#5BA4E6",
  "#2d8a5f",
  "#7a9bc7",
  "#0f1a3d",
  "#1f3b73",
  "#6aa0d6",
];

const GROWTH_BASELINE_LABEL = "Tue, 22 Jul 2024 23:11:49 GMT";
const GROWTH_BASELINE_VALUE = 1;
const GROWTH_BASELINE_TIME = Date.UTC(2024, 6, 23, 0, 0, 0);

const normalizePortfolioSymbol = (symbol) => {
  if (!symbol) {
    return "";
  }
  const upper = symbol.toUpperCase();
  if (upper === "BNSOL_BINANCE" || upper === "BNSOL") {
    return "SOL";
  }
  return upper.split("_")[0];
};

const normalizeMonitorSymbol = (symbol) => {
  if (!symbol) {
    return "";
  }
  const cleaned = symbol.replace("USD_PERP", "").replace("USDT_PERP", "");
  if (cleaned.toUpperCase() === "BNSOL") {
    return "SOL";
  }
  return cleaned.toUpperCase();
};

const combinePortfolioData = (data) => {
  const combined = (Array.isArray(data) ? data : []).reduce((acc, item) => {
    const base = normalizePortfolioSymbol(item.symbol);
    if (!base) {
      return acc;
    }
    if (!acc[base]) {
      acc[base] = {
        ...item,
        symbol: base,
        amount: parseFloat(item.amount) || 0,
        assets_value: parseFloat(item.assets_value) || 0,
        portfolio_allocation: parseFloat(item.portfolio_allocation) || 0,
      };
    } else {
      acc[base].amount += parseFloat(item.amount) || 0;
      acc[base].assets_value += parseFloat(item.assets_value) || 0;
      acc[base].portfolio_allocation += parseFloat(item.portfolio_allocation) || 0;
    }
    return acc;
  }, {});

  return Object.values(combined).sort(
    (a, b) => b.portfolio_allocation - a.portfolio_allocation
  );
};

const sortTimeSeries = (labels, datasets) => {
  const safeLabels = Array.isArray(labels) ? labels : [];
  const safeDatasets = Array.isArray(datasets) ? datasets : [];
  const points = safeLabels.map((label, index) => ({
    label,
    date: new Date(label),
    values: safeDatasets.map((dataset) => dataset?.[index]),
  }));

  points.sort((a, b) => a.date - b.date);

  return {
    labels: points.map((point) => point.label),
    datasets: safeDatasets.map((_, datasetIndex) =>
      points.map((point) => point.values[datasetIndex])
    ),
  };
};

const formatPercentValue = (value) => {
  const numeric = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return `${(numeric * 100).toFixed(2)}%`;
};

const applyGrowthBaseline = (labels, values) => {
  const safeLabels = Array.isArray(labels) ? labels : [];
  const safeValues = Array.isArray(values) ? values : [];
  const length = Math.min(safeLabels.length, safeValues.length);
  const trimmedLabels = safeLabels.slice(0, length);
  const trimmedValues = safeValues.slice(0, length);

  if (trimmedLabels.length === 0) {
    return { labels: trimmedLabels, values: trimmedValues };
  }

  const baselineTime = GROWTH_BASELINE_TIME;

  const filteredLabels = [];
  const filteredValues = [];

  for (let i = 0; i < trimmedLabels.length; i++) {
    const parsed = new Date(trimmedLabels[i]);
    if (!Number.isNaN(parsed.getTime()) && parsed.getTime() >= baselineTime) {
      filteredLabels.push(trimmedLabels[i]);
      filteredValues.push(trimmedValues[i]);
    }
  }

  if (filteredLabels.length === 0) {
    return {
      labels: [GROWTH_BASELINE_LABEL],
      values: [GROWTH_BASELINE_VALUE],
    };
  }

  const earliestDate = new Date(filteredLabels[0]);
  const hasBaseline =
    filteredLabels.findIndex(
      (label) => new Date(label).getTime() === baselineTime
    ) !== -1;

  if (
    !hasBaseline &&
    Number.isFinite(earliestDate.getTime()) &&
    earliestDate.getTime() > baselineTime
  ) {
    return {
      labels: [GROWTH_BASELINE_LABEL, ...filteredLabels],
      values: [GROWTH_BASELINE_VALUE, ...filteredValues],
    };
  }

  return { labels: filteredLabels, values: filteredValues };
};

export default function Dashboard({ showNav = false }) {
  const navigate = useNavigate();
  const { data, status } = useDashboardData();
  const [assetsRange, setAssetsRange] = useState("1M");
  const [apyRange, setApyRange] = useState("1M");
  const [growthRange, setGrowthRange] = useState("Total");

  const portfolioLatest = useMemo(
    () => (Array.isArray(data?.portfolioLatestData) ? data.portfolioLatestData : []),
    [data]
  );

  const trackSummary = useMemo(() => data?.trackRecord || null, [data]);

  const dailyPortfolio = useMemo(() => {
    if (!data?.dailyPortfolioData) {
      return null;
    }
    const sortedPortfolio = sortTimeSeries(data.dailyPortfolioData?.labels, [
      data.dailyPortfolioData?.datasets1,
      data.dailyPortfolioData?.datasets2,
      data.dailyPortfolioData?.datasets3,
      data.dailyPortfolioData?.datasets4,
    ]);
    return {
      labels: sortedPortfolio.labels,
      datasets1: sortedPortfolio.datasets[0] || [],
      datasets2: sortedPortfolio.datasets[1] || [],
      datasets3: sortedPortfolio.datasets[2] || [],
      datasets4: sortedPortfolio.datasets[3] || [],
    };
  }, [data]);

  const growthAdjusted = useMemo(() => {
    if (!data?.adjustedGrowthData) {
      return null;
    }
    const sortedGrowth = sortTimeSeries(data.adjustedGrowthData?.labels, [
      data.adjustedGrowthData?.datasets4,
    ]);
    return {
      labels: sortedGrowth.labels,
      values: sortedGrowth.datasets[0] || [],
    };
  }, [data]);

  const dailyAssets = useMemo(() => {
    if (!data?.dailyAssetsData) {
      return null;
    }
    const sortedAssets = sortTimeSeries(data.dailyAssetsData?.labels, [
      data.dailyAssetsData?.datasets1,
      data.dailyAssetsData?.datasets2,
    ]);
    return {
      labels: sortedAssets.labels,
      datasets1: sortedAssets.datasets[0] || [],
      datasets2: sortedAssets.datasets[1] || [],
    };
  }, [data]);

  const sharpeRatio = useMemo(() => {
    if (!dailyPortfolio) {
      return null;
    }
    return computeSharpeRatioFromDailyGrowth(
      dailyPortfolio.labels,
      dailyPortfolio.datasets4 || []
    );
  }, [dailyPortfolio]);

  const holdings = useMemo(() => {
    const monitorAprData = Array.isArray(data?.monitorAprData)
      ? data.monitorAprData
      : [];

    if (monitorAprData.length === 0 || portfolioLatest.length === 0) {
      return [];
    }

    const combinedPortfolio = combinePortfolioData(portfolioLatest);
    const normalizedPortfolioMap = new Map(
      combinedPortfolio.map((item) => [item.symbol, item])
    );

    if (normalizedPortfolioMap.size === 0) {
      return [];
    }

    const filteredHoldings = monitorAprData.filter((item) =>
      normalizedPortfolioMap.has(normalizeMonitorSymbol(item.symbol))
    );

    return filteredHoldings
      .map((item, index) => {
        const baseSymbol = normalizeMonitorSymbol(item.symbol);
        const portfolioItem = normalizedPortfolioMap.get(baseSymbol);
        const allocation = portfolioItem
          ? Number(portfolioItem.portfolio_allocation) * 100
          : null;

        return {
          name: baseSymbol,
          pair: `${baseSymbol}/USDT`,
          symbol: baseSymbol ? baseSymbol[0] : "—",
          color: HOLDING_COLORS[index % HOLDING_COLORS.length],
          portfolio:
            allocation !== null && Number.isFinite(allocation)
              ? `${allocation.toFixed(2)}%`
              : "—",
          expectedApy: formatPercentValue(item.expected_apy),
          latestFunding: formatPercentValue(item.next_funding_rate),
          previousFunding: formatPercentValue(item.previous_funding_rate),
          cum3d: formatPercentValue(item["3_day_cum_funding"]),
          cum7d: formatPercentValue(item["7_day_cum_funding"]),
          cum30d: formatPercentValue(item["30_day_cum_funding"]),
        };
      })
      .sort((a, b) => {
        const aValue = parseFloat(a.portfolio) || 0;
        const bValue = parseFloat(b.portfolio) || 0;
        return bValue - aValue;
      });
  }, [data, portfolioLatest]);

  const holdingsUpdatedAt = useMemo(() => {
    const monitorAprData = Array.isArray(data?.monitorAprData)
      ? data.monitorAprData
      : [];
    if (!monitorAprData[0]?.time) {
      return null;
    }
    return monitorAprData[0].time.replace("GMT", "").trim();
  }, [data]);

  const holdingsLoading = status === "loading" && holdings.length === 0;

  const portfolioAllocation = useMemo(() => {
    const combined = combinePortfolioData(portfolioLatest);
    return {
      labels: combined.map((item) => item.symbol),
      values: combined.map((item) => Number(item.portfolio_allocation) * 100),
    };
  }, [portfolioLatest]);

  const growthSeries = useMemo(() => {
    const hasAdjustedGrowth =
      growthAdjusted?.labels && growthAdjusted.labels.length > 0;
    const sourceLabels = hasAdjustedGrowth
      ? growthAdjusted.labels
      : dailyPortfolio?.labels || [];
    const sourceValues = hasAdjustedGrowth
      ? growthAdjusted.values
      : dailyPortfolio?.datasets4 || [];
    const adjusted = applyGrowthBaseline(sourceLabels, sourceValues);
    return {
      labels: adjusted.labels,
      values: adjusted.values,
    };
  }, [dailyPortfolio, growthAdjusted]);

  const assetsSeries = useMemo(() => {
    return {
      labels: dailyAssets?.labels || [],
      totalAssets: dailyAssets?.datasets2 || [],
      netExposure: dailyAssets?.datasets1 || [],
    };
  }, [dailyAssets]);

  const apySeries = useMemo(() => {
    return {
      labels: dailyPortfolio?.labels || [],
      expected: dailyPortfolio?.datasets1 || [],
      thirtyDay: dailyPortfolio?.datasets2 || [],
      ninetyDay: dailyPortfolio?.datasets3 || [],
    };
  }, [dailyPortfolio]);

  const growthPercent = useMemo(() => {
    if (trackSummary?.dollar_value_growth) {
      return (trackSummary.dollar_value_growth - 1) * 100;
    }
    if (dailyPortfolio?.datasets4?.length) {
      const latestValue =
        dailyPortfolio.datasets4[dailyPortfolio.datasets4.length - 1];
      const numeric = Number(latestValue);
      return Number.isFinite(numeric) ? (numeric - 1) * 100 : null;
    }
    return null;
  }, [trackSummary, dailyPortfolio]);

  const growthReturns = useMemo(() => {
    const raw = dailyPortfolio?.datasets4;
    if (!raw || !raw.length) return null;
    const values = raw
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n));
    if (!values.length) return null;

    const latest = values[values.length - 1];
    const first = values[0];

    const getValueDaysAgo = (days) => {
      const idx = values.length - 1 - days;
      if (idx < 0) return first;
      return values[idx];
    };

    const calcReturn = (base) => {
      if (!base || !Number.isFinite(base) || base === 0) return null;
      return (latest / base - 1) * 100;
    };

    // Total uses API/summary growth (since inception / later start), not first point in series
    const totalReturn =
      growthPercent != null ? growthPercent : calcReturn(first);

    return {
      "1M": calcReturn(getValueDaysAgo(31)),
      "3M": calcReturn(getValueDaysAgo(89)),
      "1Y": calcReturn(getValueDaysAgo(365)),
      Total: totalReturn,
    };
  }, [dailyPortfolio, growthPercent]);

  const portfolioAllocationLoading = status === "loading" && !(Array.isArray(data?.portfolioLatestData) && data.portfolioLatestData.length > 0);
  const overviewStatsLoading = status === "loading" && !data?.dailyPortfolioData;

  return (
    <div className={styles.dashboard}>

      {showNav && <NavBar activeLink="dashboard" />}

      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Portfolio Overview</h1>
            <p className="page-subtitle">Real-time performance metrics and allocation data</p>
          </div>
          <div className="header-actions">
            <a className="btn btn-secondary" href="#">
              Export Report
            </a>
            <button 
              className={`btn btn-primary ${styles.mintUsdiBtn}`}
              onClick={() => {
                const token = localStorage.getItem('gatewayToken');
                if (token) {
                  navigate(`/swap?token=${token}`);
                } else {
                  navigate('/swap');
                }
              }}
            >
              Mint USDi
            </button>
          </div>
        </div>

        <div className="portfolio-section">
          <div className={styles.portfolioAllocationWrapper}>
            <PortfolioAllocation totalValue={trackSummary?.totalValue ?? null} />
            {portfolioAllocationLoading && (
              <div className={styles.portfolioAllocationLoadingOverlay} aria-hidden="true">
                <div className={styles.loadingSpinner} />
              </div>
            )}
          </div>
          <GrowthOfOne
            growthPercent={growthPercent}
            growthReturns={growthReturns}
            growthRange={growthRange}
            onGrowthRangeChange={setGrowthRange}
          />
        </div>

        <OverviewStats
          expected30DaysEarnings={trackSummary?.expectedthirtyDayEarning ?? null}
          netExposureValue={trackSummary?.nev ?? null}
          sharpeRatio={sharpeRatio}
          loading={overviewStatsLoading}
        />

        <CurrentHoldings
          holdings={holdings}
          lastUpdated={holdingsUpdatedAt}
          loading={holdingsLoading}
        />

        <div className="charts-section">
          <AssetsHistory range={assetsRange} onRangeChange={setAssetsRange} />
          <ProtocolApyHistory range={apyRange} onRangeChange={setApyRange} />
        </div>

        <div className="dashboard-footer">
          <span className="footer-text">
            &copy; 2025 Cutwater Partners. All rights reserved.
          </span>
        </div>
      </main>
      <DashboardCharts
        portfolioAllocation={portfolioAllocation}
        growthSeries={growthSeries}
        growthRange={growthRange}
        assetsSeries={assetsSeries}
        apySeries={apySeries}
        assetsRange={assetsRange}
        apyRange={apyRange}
      />
    </div>
  );
}
